import React, { useState, useEffect } from 'react';
import "../../css/CreateRoutine.css";
import Loader from "../../components/Loader";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaCalendarAlt, FaEdit, FaTrash, FaSearch, FaTimes, FaClock, FaPlus, FaSave } from 'react-icons/fa';

function CreateRoutine() {
    const [classes, setClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [routineData, setRoutineData] = useState({
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
    });
    const [showForm, setShowForm] = useState(false);
    const [currentDay, setCurrentDay] = useState('');
    const [newPeriod, setNewPeriod] = useState({
        subject: '',
        teacher: '',
        startTime: '',
        endTime: '',
    });
    const [editIndex, setEditIndex] = useState(null);

    useEffect(() => {
        fetchClasses();
    }, []);

    const fetchClasses = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/classes", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch classes");
            }

            const data = await response.json();
            setClasses(data);
        } catch (error) {
            console.error("Error fetching classes:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };


    const fetchExistingRoutine = async (classId) => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch(`https://schoolpro-api.uniums.com/api/routines/class/${classId}`, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (response.status === 404) {
                // No routine exists yet, use empty template
                setRoutineData({
                    monday: [],
                    tuesday: [],
                    wednesday: [],
                    thursday: [],
                    friday: [],
                    saturday: [],
                });
                return;
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch routine");
            }

            const data = await response.json();
            setRoutineData(data.schedule);
        } catch (error) {
            console.error("Error fetching routine:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };


    const handleClassSelect = (e) => {
        const classId = e.target.value;
        setSelectedClass(classId);

        if (classId) {
            fetchExistingRoutine(classId);
        } else {
            setRoutineData({
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
            });
        }
    };
    const validateTimeConflict = async (day, newPeriod, editIndex) => {
        const token = localStorage.getItem("token");
        if (!token) return false;

        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/routines/validate-periods", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    day,
                    periods: [...routineData[day], newPeriod],
                    currentPeriodIndex: editIndex !== null ? editIndex : routineData[day].length
                })
            });

            const data = await response.json();
            return data.valid;
        } catch (error) {
            console.error("Error validating time conflicts:", error);
            return false;
        }
    };
    const addNewPeriod = (day) => {
        setCurrentDay(day);
        setNewPeriod({
            subject: '',
            teacher: '',
            startTime: '',
            endTime: '',
        });
        setEditIndex(null);
        setShowForm(true);
    };

    const editPeriod = (day, index) => {
        setCurrentDay(day);
        setNewPeriod({ ...routineData[day][index] });
        setEditIndex(index);
        setShowForm(true);
    };

    const deletePeriod = (day, index) => {
        const updatedDay = [...routineData[day]];
        updatedDay.splice(index, 1);
        setRoutineData({
            ...routineData,
            [day]: updatedDay
        });
        toast.success("Period removed successfully!");
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewPeriod({
            ...newPeriod,
            [name]: value
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate times
        const startTime = new Date(`2000-01-01T${newPeriod.startTime}`);
        const endTime = new Date(`2000-01-01T${newPeriod.endTime}`);

        if (endTime <= startTime) {
            toast.error("End time must be after start time");
            return;
        }

        // Check for time conflicts
        const dayPeriods = routineData[currentDay].filter((_, index) => index !== editIndex);
        const hasConflict = dayPeriods.some(period => {
            const periodStart = new Date(`2000-01-01T${period.startTime}`);
            const periodEnd = new Date(`2000-01-01T${period.endTime}`);

            return (startTime < periodEnd && endTime > periodStart);
        });

        if (hasConflict) {
            toast.error("Time conflict with another period");
            return;
        }

        if (editIndex !== null) {
            // Update existing period
            const updatedDay = [...routineData[currentDay]];
            updatedDay[editIndex] = newPeriod;

            setRoutineData({
                ...routineData,
                [currentDay]: updatedDay
            });
            toast.success("Period updated successfully!");
        } else {
            // Add new period
            setRoutineData({
                ...routineData,
                [currentDay]: [...routineData[currentDay], newPeriod]
            });
            toast.success("Period added successfully!");
        }

        setShowForm(false);
    };

    const cancelForm = () => {
        setShowForm(false);
    };

    const saveRoutine = async () => {
        if (!selectedClass) {
            toast.error("Please select a class first!");
            return;
        }

        setIsLoading(true);

        // Prepare routine data for API
        const routinePayload = {
            classId: selectedClass,
            schedule: routineData
        };

        const token = localStorage.getItem("token");

        try {
            // Replace with your actual API endpoint
            const response = await fetch("https://schoolpro-api.uniums.com/api/routines/create", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(routinePayload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to save routine");
            }

            toast.success("Routine saved successfully!");
        } catch (error) {
            console.error("Error saving routine:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const sortPeriods = (periods) => {
        return [...periods].sort((a, b) => {
            const timeA = new Date(`2000-01-01T${a.startTime}`);
            const timeB = new Date(`2000-01-01T${b.startTime}`);
            return timeA - timeB;
        });
    };

    const renderDaySchedule = (day) => {
        const sortedPeriods = sortPeriods(routineData[day]);

        return (
            <div className="rutineCreate-day-container">
                <div className="rutineCreate-day-header">
                    <h3 className="rutineCreate-day-title">{day.charAt(0).toUpperCase() + day.slice(1)}</h3>
                    <button
                        className="rutineCreate-btn rutineCreate-btn-add"
                        onClick={() => addNewPeriod(day)}
                        disabled={!selectedClass}
                    >
                        <FaPlus /> Add Period
                    </button>
                </div>

                {sortedPeriods.length === 0 ? (
                    <div className="rutineCreate-empty-day">
                        <p>No periods added</p>
                    </div>
                ) : (
                    <div className="rutineCreate-periods-container">
                        {sortedPeriods.map((period, index) => (
                            <div key={index} className="rutineCreate-period-card">
                                <div className="rutineCreate-period-time">
                                    <FaClock className="rutineCreate-time-icon" />
                                    <span>{period.startTime} - {period.endTime}</span>
                                </div>
                                <div className="rutineCreate-period-details">
                                    <h4 className="rutineCreate-subject-name">{period.subject}</h4>
                                    <p className="rutineCreate-teacher-name">Teacher: {period.teacher}</p>
                                </div>
                                <div className="rutineCreate-period-actions">
                                    <button
                                        className="rutineCreate-btn rutineCreate-btn-edit rutineCreate-btn-sm"
                                        onClick={() => editPeriod(day, index)}
                                    >
                                        <FaEdit />
                                    </button>
                                    <button
                                        className="rutineCreate-btn rutineCreate-btn-delete rutineCreate-btn-sm"
                                        onClick={() => deletePeriod(day, index)}
                                    >
                                        <FaTrash />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="rutineCreate-container">
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />

            {isLoading && <Loader />}

            <div className="rutineCreate-header">
                <div className="rutineCreate-title-container">
                    <FaCalendarAlt className="rutineCreate-title-icon" />
                    <h1 className="rutineCreate-title">Class Routine Management</h1>
                </div>

                <div className="rutineCreate-controls">
                    <div className="rutineCreate-select-container">
                        <select
                            className="rutineCreate-select"
                            value={selectedClass}
                            onChange={handleClassSelect}
                        >
                            <option value="">Select a Class</option>
                            {classes.map(cls => (
                                <option key={cls._id} value={cls._id}>
                                    {cls.name} - {cls.section}
                                </option>
                            ))}
                        </select>
                    </div>

                    <button
                        className="rutineCreate-btn rutineCreate-btn-save"
                        onClick={saveRoutine}
                        disabled={!selectedClass}
                    >
                        <FaSave /> Save Routine
                    </button>
                </div>
            </div>

            {!selectedClass ? (
                <div className="rutineCreate-select-class-prompt">
                    <div className="rutineCreate-prompt-icon">
                        <FaCalendarAlt />
                    </div>
                    <h2>Please select a class to create or edit its routine</h2>
                    <p>You can manage class schedules for each day of the week</p>
                </div>
            ) : (
                <div className="rutineCreate-schedule-builder">
                    <div className="rutineCreate-days-grid">
                        {Object.keys(routineData).map(day => (
                            <div key={day} className="rutineCreate-day-column">
                                {renderDaySchedule(day)}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Period Form Modal */}
            {showForm && (
                <div className="rutineCreate-modal-overlay">
                    <div className="rutineCreate-modal">
                        <div className="rutineCreate-modal-header">
                            <h2 className="rutineCreate-modal-title">
                                {editIndex !== null ? 'Edit Period' : 'Add New Period'} - {currentDay.charAt(0).toUpperCase() + currentDay.slice(1)}
                            </h2>
                            <button className="rutineCreate-close-btn" onClick={cancelForm}>
                                <FaTimes />
                            </button>
                        </div>

                        <div className="rutineCreate-modal-body">
                            <form className="rutineCreate-period-form" onSubmit={handleSubmit}>
                                <div className="rutineCreate-form-group">
                                    <label className="rutineCreate-label">Subject Name*</label>
                                    <input
                                        type="text"
                                        name="subject"
                                        value={newPeriod.subject}
                                        onChange={handleInputChange}
                                        className="rutineCreate-input"
                                        placeholder="e.g. Mathematics"
                                        required
                                    />
                                </div>

                                <div className="rutineCreate-form-group">
                                    <label className="rutineCreate-label">Teacher Name*</label>
                                    <input
                                        type="text"
                                        name="teacher"
                                        value={newPeriod.teacher}
                                        onChange={handleInputChange}
                                        className="rutineCreate-input"
                                        placeholder="e.g. Mr. John Smith"
                                        required
                                    />
                                </div>

                                <div className="rutineCreate-form-row">
                                    <div className="rutineCreate-form-group rutineCreate-form-group-half">
                                        <label className="rutineCreate-label">Start Time*</label>
                                        <input
                                            type="time"
                                            name="startTime"
                                            value={newPeriod.startTime}
                                            onChange={handleInputChange}
                                            className="rutineCreate-input"
                                            required
                                        />
                                    </div>

                                    <div className="rutineCreate-form-group rutineCreate-form-group-half">
                                        <label className="rutineCreate-label">End Time*</label>
                                        <input
                                            type="time"
                                            name="endTime"
                                            value={newPeriod.endTime}
                                            onChange={handleInputChange}
                                            className="rutineCreate-input"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="rutineCreate-form-actions">
                                    <button type="submit" className="rutineCreate-btn rutineCreate-btn-success">
                                        {editIndex !== null ? 'Update Period' : 'Add Period'}
                                    </button>
                                    <button
                                        type="button"
                                        className="rutineCreate-btn rutineCreate-btn-cancel"
                                        onClick={cancelForm}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CreateRoutine;